import { fromZonedTime } from 'date-fns-tz';

export interface TimeZoneOption {
  value: string;
  label: string;
}

// Canonical curated zone list — the single source shared by the AI Analyst chart
// picker (GraphChart.tsx) and HistoricalDatasetModal.tsx. A full
// Intl.supportedValuesOf('timeZone') list runs to ~400 entries, nearly all
// irrelevant to power-grid/weather data, which skews North American.
export const TIME_ZONE_OPTIONS: TimeZoneOption[] = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/St_Johns', label: 'Newfoundland Time (NST/NDT)' },
  { value: 'America/Halifax', label: 'Atlantic Time (AST/ADT)' },
  { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
  { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
  { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST, no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKST/AKDT)' },
  { value: 'America/Adak', label: 'Hawaii-Aleutian Time (HST/HDT)' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
  { value: 'America/Winnipeg', label: 'Winnipeg (CST/CDT)' },
  { value: 'America/Edmonton', label: 'Edmonton (MST/MDT)' },
  { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  { value: 'Europe/London', label: 'London Time (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET/CEST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
];

const SUPPORTED_TIME_ZONES = new Set(TIME_ZONE_OPTIONS.map((option) => option.value));

// Guards against a stale zone in localStorage reaching Intl and throwing
export const isSupportedTimeZone = (zone: string | null | undefined): boolean =>
  !!zone && SUPPORTED_TIME_ZONES.has(zone);

const LOCAL_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

// Reads 'YYYY-MM-DDTHH:mm' as wall-clock time in `zone` → UTC ISO; DST-aware, null if unusable
export const zonedDateTimeToUtcIso = (
  localDateTime: string | null | undefined,
  zone: string
): string | null => {
  if (!localDateTime || !LOCAL_DATE_TIME_PATTERN.test(localDateTime)) return null;
  if (!isSupportedTimeZone(zone)) return null;
  try {
    const utcDate = fromZonedTime(localDateTime, zone);
    return Number.isNaN(utcDate.getTime()) ? null : utcDate.toISOString();
  } catch {
    return null;
  }
};
